import { Survey } from '../models/Survey'
import {
  CareerCategoryConfig,
  defaultCareerCategories,
} from '../models/CareerCategory'

export interface CareerCategoryDisplay {
  number: number
  categoryLabel: string
  description: string
  nursingJobs: string[]
}

export interface CareerCategoryResult {
  matchedNumbers: number[]
  displayCategory: CareerCategoryDisplay
  displayNursingJobs: string[]
  hasMultiple: boolean
}

interface SurveyMap {
  career_calling?: Survey
  outcome_expectation?: Survey
  bfi15?: Survey
}

/** career_calling 構面 → callingBase（高結果預期 1-20） */
const CALLING_BASE_HIGH: Record<string, number> = {
  interest_passion: 1,
  existential_meaning: 6,
  altruism: 11,
  transcendence: 16,
}

/** career_calling 構面 → callingBase（低結果預期 21-40） */
const CALLING_BASE_LOW: Record<string, number> = {
  interest_passion: 21,
  existential_meaning: 26,
  altruism: 31,
  transcendence: 36,
}

/** bfi15 構面 → personalityOffset (0-4) */
const PERSONALITY_OFFSET: Record<string, number> = {
  extraversion: 0,
  agreeableness: 1,
  conscientiousness: 2,
  neuroticism: 3,
  openness: 4,
}

const CALLING_DIMENSIONS = [
  'interest_passion',
  'existential_meaning',
  'altruism',
  'transcendence',
] as const

const PERSONALITY_DIMENSIONS = [
  'extraversion',
  'agreeableness',
  'conscientiousness',
  'neuroticism',
  'openness',
] as const

/** 取出 scores 中分數最高的所有 key（同分時回傳多個） */
function getTopDimensions(
  scores: Record<string, number>,
  candidates: readonly string[]
): string[] {
  const filtered = candidates.filter((key) => scores[key] !== undefined)
  if (filtered.length === 0) return []
  let max = -Infinity
  for (const key of filtered) {
    const v = scores[key]
    if (v > max) max = v
  }
  return filtered.filter((key) => scores[key] === max)
}

/**
 * 根據三份量表計算護理職涯分類。
 * - 任何一份量表缺失即回傳 null（呼叫端負責顯示提示）
 * - 同分時取笛卡兒積，displayCategory 取 number 最小者，nursingJobs 合併去重
 */
export function calculateCareerCategory(
  surveys: SurveyMap,
  categories: CareerCategoryConfig[]
): CareerCategoryResult | null {
  const { career_calling, outcome_expectation, bfi15 } = surveys
  if (!career_calling || !outcome_expectation || !bfi15) return null

  // 1. outcome_expectation 5 構面加總，>= 25 → 高結果預期
  const oeScores = outcome_expectation.scores
  const oeSum =
    (oeScores.security ?? 0) +
    (oeScores.empowerment ?? 0) +
    (oeScores.helping_others ?? 0) +
    (oeScores.flexibility ?? 0) +
    (oeScores.opportunity ?? 0)
  const isHigh = oeSum >= 25
  const callingBaseMap = isHigh ? CALLING_BASE_HIGH : CALLING_BASE_LOW

  // 2. career_calling 最高分構面
  const topCallings = getTopDimensions(
    career_calling.scores,
    CALLING_DIMENSIONS
  )
  // 3. bfi15 最高分構面
  const topPersonalities = getTopDimensions(
    bfi15.scores,
    PERSONALITY_DIMENSIONS
  )

  if (topCallings.length === 0 || topPersonalities.length === 0) return null

  // 4. 笛卡兒積算 number
  const matched = new Set<number>()
  for (const c of topCallings) {
    const base = callingBaseMap[c]
    if (base === undefined) continue
    for (const p of topPersonalities) {
      const offset = PERSONALITY_OFFSET[p]
      if (offset === undefined) continue
      matched.add(base + offset)
    }
  }
  if (matched.size === 0) return null

  const matchedNumbers = Array.from(matched).sort((a, b) => a - b)

  // 5. 取分類資料：優先 Firestore，fallback 預設值
  const lookup = (num: number): CareerCategoryDisplay => {
    const fromStore = categories.find((c) => c.number === num)
    if (fromStore) {
      return {
        number: fromStore.number,
        categoryLabel: fromStore.categoryLabel,
        description: fromStore.description,
        nursingJobs: fromStore.nursingJobs,
      }
    }
    const def = defaultCareerCategories.find((c) => c.number === num)
    if (def) {
      return {
        number: def.number,
        categoryLabel: def.categoryLabel,
        description: def.description,
        nursingJobs: [...def.nursingJobs],
      }
    }
    return {
      number: num,
      categoryLabel: '',
      description: '',
      nursingJobs: [],
    }
  }

  const displayCategory = lookup(matchedNumbers[0])

  // 合併所有 matched number 的 nursingJobs 去重
  const jobSet = new Set<string>()
  for (const num of matchedNumbers) {
    const cat = lookup(num)
    cat.nursingJobs.forEach((j) => jobSet.add(j))
  }

  return {
    matchedNumbers,
    displayCategory,
    displayNursingJobs: Array.from(jobSet),
    hasMultiple: matchedNumbers.length > 1,
  }
}
